/* eslint-disable @typescript-eslint/no-unused-vars */
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import HistoryIcon from "@mui/icons-material/History";
import InfoIcon from "@mui/icons-material/Info";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  GetAllWarehouseApiResponse,
  Warehouse,
} from "../../../@types/DRO/GetAllWarehouseResponse";
import { TransferOrderPartDetails } from "../../../@types/DRO/GetPartsListResponse";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";
import { DownloadPdfApi } from "../../../api/PdfDownload/DownloadPdfApi";
import { TransferOrderApi } from "../../../api/TransferOrder/TransferOrderApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import {
  AppAutoCompleteWithSearchAPI,
  OptionType,
} from "../../Layouts/AppAutoCompleteWithSearchAPI/AppAutoCompleteWithSearchAPI";
import VendorAllocation, {
  VendorOption,
  VendorSplitEntry,
} from "../../Layouts/VendorAllocation/VendorAllocation";
import { TransferOrderHistory } from "../OrderHistory/TransferOrderHistory";

// ---- Vendor split validation (same rules/messages as CreateTransferOrder) ----
type VendorSplitErrors = {
  rows: { [splitId: number]: string };
  section?: string;
};

const VENDOR_ROW_ERROR = "Please select Vendor and fill the quantities";
const VENDOR_QUANTITY_ERROR =
  "Vendor quantity should be less than or equal to total quantity.";

const getVendorSplitErrors = (
  mainQuantity: number,
  splits: VendorSplitEntry[]
): VendorSplitErrors | null => {
  const rows: VendorSplitErrors["rows"] = {};

  splits.forEach((split) => {
    const qty = Number(split.quantity) || 0;
    const noVendor =
      split.vendorId === "" || split.vendorId === null || split.vendorId === undefined;
    if (noVendor || qty <= 0) {
      rows[split.id] = VENDOR_ROW_ERROR;
    }
  });

  return Object.keys(rows).length > 0 ? { rows } : null;
};

const DraftTransferOrder: React.FC = () => {
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const params = useParams<{ orderId: string }>();
  const [items, setItems] = useState<TransferOrderPartDetails[]>([]);
  const [warehouses, setWarehouses] = useState<
    GetAllWarehouseApiResponse["data"]["data"]
  >([]);
  const [destinationWarehouses, setDestinationWarehouses] = useState<
    GetAllWarehouseApiResponse["data"]["data"]
  >([]);
  const [sourceWarehouse, setSourceWarehouse] = useState<Warehouse | null>(
    null
  );
  const [destinationWarehouse, setDestinationWarehouse] =
    useState<Warehouse | null>(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [destinationPageNumber, setDestinationPageNumber] = useState(1);
  const rowsPerPage = 10;
  const searchQuery = "";

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [permittedActions, setPermittedActions] = useState<string[]>([]);
  const [transitWarnings, setTransitWarnings] = useState<{
    [key: number]: { message: string; quantity: number };
  }>({});
  const [transferOrderDetails, setTransferOrderDetails] = useState<any>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  // ---- Vendor state ----
  const [vendorAllocationOptions, setVendorAllocationOptions] = useState<VendorOption[]>([]);
  const [isLoadingVendorOptions, setIsLoadingVendorOptions] = useState(false);
  const [splitActiveRows, setSplitActiveRows] = useState<{
    [key: number]: boolean;
  }>({});
  const [vendorSplits, setVendorSplits] = useState<{
    [key: number]: VendorSplitEntry[];
  }>({});
  const [vendorSplitErrors, setVendorSplitErrors] = useState<{
    [itemIndex: number]: VendorSplitErrors;
  }>({});

  const fetchVendorOptionsForWarehouse = useCallback(async (warehouseId: number) => {
    try {
      setIsLoadingVendorOptions(true);
      const response = await AllPartApi.api.getPartList(
        0,
        10,
        "",
        undefined,
        undefined,
        undefined,
        warehouseId
      );

      const parts = response?.data?.data || [];
      const vendorMap = new Map<number, VendorOption>();
      parts.forEach((part) => {
        (part.vendorAllocations || []).forEach((allocation: any) => {
          if (allocation?.vendorId != null && !vendorMap.has(allocation.vendorId)) {
            vendorMap.set(allocation.vendorId, {
              productId: part.id,
              vendorId: allocation.vendorId,
              vendorName: allocation.vendorName,
              quantity: allocation.quantity || 0,
            });
          }
        });
      });

      setVendorAllocationOptions(Array.from(vendorMap.values()));
    } catch (error) {
      console.error("Error fetching vendor options", error);
      setVendorAllocationOptions([]);
    } finally {
      setIsLoadingVendorOptions(false);
    }
  }, []);

  // Source warehouse is populated from the order details API in this screen,
  // so this also runs once the draft has loaded.
  useEffect(() => {
    if (sourceWarehouse) {
      fetchVendorOptionsForWarehouse(sourceWarehouse.id);
    } else {
      setVendorAllocationOptions([]);
    }
  }, [sourceWarehouse, fetchVendorOptionsForWarehouse]);

  const validateVendorSplits = (): boolean => {
    const errors: typeof vendorSplitErrors = {};
    let hasExcessVendorQuantity = false;
    items.forEach((item, index) => {
      if (!splitActiveRows[index]) return; // only items with vendor allocation enabled
      const splits = vendorSplits[index] || [];
      const totalVendorQuantity = splits.reduce(
        (total, split) => total + (Number(split.quantity) || 0),
        0
      );
      if (totalVendorQuantity > item.quantity) {
        hasExcessVendorQuantity = true;
      }

      const result = getVendorSplitErrors(item.quantity, splits);
      if (result) errors[index] = result;
    });
    setVendorSplitErrors(errors);
    if (hasExcessVendorQuantity) {
      setSnackbarMessage(VENDOR_QUANTITY_ERROR);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return false;
    }
    return Object.keys(errors).length === 0;
  };

  // Clears errors live as the user fixes them (only re-checks rows that currently show an error)
  useEffect(() => {
    setVendorSplitErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next: typeof prev = {};
      Object.keys(prev).forEach((key) => {
        const i = Number(key);
        if (!splitActiveRows[i] || !items[i]) return;
        const result = getVendorSplitErrors(items[i].quantity, vendorSplits[i] || []);
        if (result) next[i] = result;
      });
      return next;
    });
  }, [vendorSplits, items, splitActiveRows]);

  // Vendor options come from the source warehouse's parts. A vendor already saved on this
  // draft may not be in that list, so add it for the row to keep it selectable/displayed.
  const getVendorOptionsForRow = (index: number, productId: number): VendorOption[] => {
    const extra: VendorOption[] = [];
    (vendorSplits[index] || []).forEach((split) => {
      if (!split.vendorId) return;
      // VendorAllocation only lists options whose productId matches the row's product
      const alreadyListed =
        vendorAllocationOptions.some(
          (o) => o.productId === productId && o.vendorId.toString() === split.vendorId
        ) || extra.some((o) => o.vendorId.toString() === split.vendorId);
      if (!alreadyListed) {
        extra.push({
          productId,
          vendorId: Number(split.vendorId),
          vendorName: split.vendorName,
          quantity: Number(split.quantity) || 0,
        });
      }
    });
    return extra.length > 0 ? [...extra, ...vendorAllocationOptions] : vendorAllocationOptions;
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleDownloadPdf = async () => {
    if (!params.orderId) {
      setSnackbarMessage("Transfer Order ID is required to download PDF");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsDownloading(true);
      await DownloadPdfApi.api.getPdfLinkByTOId(Number(params.orderId));
      setSnackbarMessage("PDF downloaded successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error("Error downloading PDF:", error);
      setSnackbarMessage(
        error?.response?.data?.message ||
          error?.data?.message ||
          error?.message ||
          "Failed to download PDF. Please try again."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleScroll = (event: React.SyntheticEvent) => {
    const bottom =
      event.currentTarget.scrollHeight - event.currentTarget.scrollTop ===
      event.currentTarget.clientHeight;
    if (bottom) {
      setCurrentPageNumber((prevPage) => prevPage + 1);
    }
  };

  const handleDestinationScroll = (event: React.SyntheticEvent) => {
    const bottom =
      event.currentTarget.scrollHeight - event.currentTarget.scrollTop ===
      event.currentTarget.clientHeight;
    if (bottom) {
      setDestinationPageNumber((prevPage) => prevPage + 1);
    }
  };

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
        currentPageNumber,
        rowsPerPage,
        searchQuery
      );
      setWarehouses((prev) => {
        const set = new Set<number>();
        return [...prev, ...response.data.data].filter(
          (response: Warehouse) => !set.has(response.id) && set.add(response.id)
        );
      });
    } catch (error) {
      console.error("Error fetching warehouses", error);
    }
  }, [currentPageNumber]);

  const fetchDestinationWarehouses = useCallback(async () => {
    try {
      const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
        destinationPageNumber,
        rowsPerPage,
        searchQuery,
        true // myWarehouseOnly parameter set to true
      );
      setDestinationWarehouses((prev) => {
        const set = new Set<number>();
        return [...prev, ...response.data.data].filter(
          (response: Warehouse) => !set.has(response.id) && set.add(response.id)
        );
      });
    } catch (error) {
      console.error("Error fetching destination warehouses", error);
    }
  }, [destinationPageNumber]);

  function fetchPaginatedPartsBySearchTerm(
    currentPageNumber: number,
    currentSearchQueryTerm: string
  ): Promise<{
    options: OptionType<TransferOrderPartDetails>[];
    hasMore: boolean;
  }> {
    return AllPartApi.api
      .getPartList(
        currentPageNumber,
        rowsPerPage,
        currentSearchQueryTerm,
        undefined,
        undefined,
        undefined
      )
      .then(({ data: response }) => ({
        options: response.data.map((part) => ({
          value: part.id.toString(),
          label: part.description,
          data: part,
        })),
        hasMore: currentPageNumber < response.totalPages - 1,
      }))
      .catch(() => ({ options: [], hasMore: false }));
  }

  const handlePartSelect =
    (index: number) =>
    async (event: any, value: OptionType<TransferOrderPartDetails> | null) => {
      if (value) {
        if (value.data) {
          const partDetails = value.data;
          const newItems = [...items];

          newItems[index] = {
            ...newItems[index],
            description: partDetails.description || "",
            referenceCode: partDetails.referenceCode || "",
            id: partDetails.id,
            price: +(partDetails.costPrice?.toString() || "0"),
            tax: partDetails.tax || 0,
            quantity: newItems[index].quantity || 1, // Preserve existing quantity on draft
            vendorAllocations: partDetails.vendorAllocations || [], // Preserve vendor allocations
          };

          setItems(newItems);

          // Hide the Vendor Allocation section and clear its values for this row
          // since the selected product changed
          setSplitActiveRows((prev) => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });
          setVendorSplits((prev) => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });

          // Check part transfer transition
          if (sourceWarehouse && destinationWarehouse && partDetails.id) {
            try {
              const response =
                await TransferOrderApi.api.checkPartTransferTransition({
                  sourceWarehouseId: sourceWarehouse.id,
                  destinationWarehouseId: destinationWarehouse.id,
                  partId: partDetails.id,
                });

              if (response.data.quantityInTransit > 0) {
                setTransitWarnings((prev) => ({
                  ...prev,
                  [index]: {
                    message: response.message,
                    quantity: response.data.quantityInTransit,
                  },
                }));
              } else {
                // Remove warning if quantity is 0
                setTransitWarnings((prev) => {
                  const newWarnings = { ...prev };
                  delete newWarnings[index];
                  return newWarnings;
                });
              }
            } catch (error) {
              console.error("Error checking part transfer transition:", error);
            }
          }
        }
      } else {
        // Reset the item when a part is deselected
        const newItems = [...items];
        newItems[index] = {
          description: "",
          referenceCode: "",
          costPrice: 0,
          averageCostPrice: 0,
          deleted: false,
          externalId: 0,
          id: 0,
          price: 0,
          quantity: 0,
          salesPrice: 0,
          penaltyType: "",
          status: 0,
          tax: 0,
          type: "",
          updatedBy: 0,
        };

        setItems(newItems);

        setTransitWarnings((prev) => {
          const newWarnings = { ...prev };
          delete newWarnings[index];
          return newWarnings;
        });

        // Hide the Vendor Allocation section and clear its values for this row
        // since the product was deselected
        setSplitActiveRows((prev) => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });
        setVendorSplits((prev) => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });
      }
    };

  // Fetch warehouses on component mount
  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  // Fetch destination warehouses when source is selected
  useEffect(() => {
    if (sourceWarehouse) {
      fetchDestinationWarehouses();
    }
  }, [sourceWarehouse, fetchDestinationWarehouses]);

  // Fetch transfer order details
  useEffect(() => {
    if (!params.orderId) {
      return;
    }
    setIsFetching(true);
    TransferOrderApi.api
      .getTransferOrderDetailsByTransferId(+params.orderId)
      .then((apiResponse) => {
        setPermittedActions(apiResponse.data.permittedAction || []);
        setSourceWarehouse(apiResponse.data.sourceWarehouse);
        setDestinationWarehouse(apiResponse.data.destinationWarehouse);
        setTransferOrderDetails(apiResponse.data); // <-- Store full details

        // Initialize items from order details
        const orderItems = apiResponse.data.orderLineDetailsList.map(
          (partDetails) =>
            ({
              quantity:
                partDetails.quantityReceived || partDetails.quantityOrdered,
              price: partDetails.price,
              tax: partDetails.tax || 0,
              id: partDetails.partId,
              description: partDetails.description,
              referenceCode: partDetails.referenceCode || "",
              costPrice: partDetails.price,
              deleted: false,
              externalId: 0,
              salesPrice: 0,
              penaltyType: "",
              status: 0,
              type: "",
              updatedBy: 0,
            } as TransferOrderPartDetails)
        );

        // Restore any vendor allocations saved on the draft (open the section for those rows)
        const restoredActiveRows: { [key: number]: boolean } = {};
        const restoredSplits: { [key: number]: VendorSplitEntry[] } = {};
        apiResponse.data.orderLineDetailsList.forEach(
          (partDetails: any, rowIndex: number) => {
            const saved: any[] = partDetails.vendorAllocations || [];
            if (saved.length === 0) return;
            restoredActiveRows[rowIndex] = true;
            restoredSplits[rowIndex] = saved.map(
              (allocation: any, splitIndex: number) => ({
                id: Date.now() + splitIndex + Math.random(),
                // VendorAllocation compares vendorId as a string
                vendorId:
                  allocation.vendorId != null ? String(allocation.vendorId) : "",
                vendorName: allocation.vendorName || "",
                quantity: Number(allocation.quantity) || 0,
                unitPrice: partDetails.price || 0,
              })
            );
          }
        );
        setSplitActiveRows(restoredActiveRows);
        setVendorSplits(restoredSplits);
        setVendorSplitErrors({});

        // Ensure at least one empty row
        if (orderItems.length === 0) {
          orderItems.push({
            description: "",
            referenceCode: "",
            quantity: 0,
            price: 0,
            tax: 0,
            id: 0,
            externalId: 0,
            type: "",
            status: 0,
            costPrice: 0,
            averageCostPrice: 0,
            salesPrice: 0,
            penaltyType: "",
            deleted: false,
            updatedBy: 0,
          });
        }

        setItems(orderItems);
      })
      .catch((error: any) => {
        console.error("Error fetching transfer order details:", error);
        setSnackbarMessage(
          error?.response?.data?.message ||
            error?.data?.message ||
            error?.message ||
            "Failed to fetch transfer order details"
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => {
        setIsFetching(false);
      });
  }, [params.orderId]);

  const handleUpdateTransferOrder = async (status: string) => {
    if (!params.orderId) {
      return;
    }

    setLoading(true);
    if (!sourceWarehouse || !destinationWarehouse) {
      setSnackbarMessage("Please select source and destination warehouses");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    if (!validateVendorSplits()) {
      setLoading(false);
      return;
    }

    try {
      const response = await TransferOrderApi.api.updateTransferOrder(
        +params.orderId,
        {
          sourceWarehouse: sourceWarehouse?.id,
          destinationWarehouse: destinationWarehouse.id,
          status,
          partsList: items
            .filter((item) => item.id > 0)
            .map((item) => ({
              partId: item.id || 0,
              quantityOrdered: item.quantity || 0,
              vendorAllocations: vendorSplits[items.indexOf(item)]?.map((split) => ({
                vendorId: split.vendorId,
                quantity: split.quantity,
              })),
            })),
        }
      );

      const successMessage =
        response?.message || "Transfer order updated successfully";
      setSnackbarMessage(successMessage);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      setTimeout(() => {
        navigate(`/transfer-order${statusFilter ? `?status=${statusFilter}` : ''}`);
      }, 1000);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Error updating transfer order";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        description: "",
        referenceCode: "",
        quantity: 0,
        price: 0,
        tax: 0,
        id: 0,
        externalId: 0,
        type: "",
        status: 0,
        costPrice: 0,
        averageCostPrice: 0,
        salesPrice: 0,
        penaltyType: "",
        deleted: false,
        updatedBy: 0,
      },
    ]);
  };

  const handleDeleteItem = (index: number) => {
    setVendorSplitErrors({});
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);

    // Remove any transit warnings for deleted items
    setTransitWarnings((prev) => {
      const newWarnings = { ...prev };
      // Adjust indices for warnings after the deleted item
      const adjustedWarnings: typeof newWarnings = {};
      Object.entries(newWarnings).forEach(([key, value]) => {
        const warningIndex = parseInt(key);
        if (warningIndex < index) {
          adjustedWarnings[warningIndex] = value;
        } else if (warningIndex > index) {
          adjustedWarnings[warningIndex - 1] = value;
        }
      });
      return adjustedWarnings;
    });

    // Adjust split state indices for the deleted item
    setSplitActiveRows((prev) => {
      const updated: typeof prev = {};
      Object.entries(prev).forEach(([key, value]) => {
        const rowIndex = parseInt(key);
        if (rowIndex < index) {
          updated[rowIndex] = value;
        } else if (rowIndex > index) {
          updated[rowIndex - 1] = value;
        }
      });
      return updated;
    });

    setVendorSplits((prev) => {
      const updated: typeof prev = {};
      Object.entries(prev).forEach(([key, value]) => {
        const rowIndex = parseInt(key);
        if (rowIndex < index) {
          updated[rowIndex] = value;
        } else if (rowIndex > index) {
          updated[rowIndex - 1] = value;
        }
      });
      return updated;
    });
  };

  // ---- Vendor split handlers ----
  const handleToggleSplitVendor = (index: number) => {
    const isCurrentlyActive = !!splitActiveRows[index];

    if (isCurrentlyActive) {
      // Second click - close/hide the split section
      setSplitActiveRows((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
      setVendorSplits((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
      return;
    }

    // First click - open the split section with one vendor row
    setSplitActiveRows((prev) => ({ ...prev, [index]: true }));
    setVendorSplits((prev) => {
      if (prev[index] && prev[index].length >= 1) return prev;
      return {
        ...prev,
        [index]: [
          {
            id: Date.now(),
            vendorId: "",
            vendorName: "",
            quantity: items[index]?.quantity || 0,
            unitPrice: items[index]?.price || 0,
          },
        ],
      };
    });
  };

  const handleAddVendorSplitRow = (index: number) => {
    setVendorSplits((prev) => {
      const existing = prev[index] || [];
      return {
        ...prev,
        [index]: [
          ...existing,
          {
            id: Date.now() + Math.random(),
            vendorId: "",
            vendorName: "",
            quantity: 0,
            unitPrice: items[index]?.price || 0,
          },
        ],
      };
    });
  };

  const handleRemoveVendorSplitRow = (index: number, splitId: number) => {
    setVendorSplits((prev) => {
      const existing = prev[index] || [];
      // Keep a minimum of 1 vendor row in the split section
      if (existing.length <= 1) return prev;
      const updated = existing.filter((split) => split.id !== splitId);
      return { ...prev, [index]: updated };
    });
  };

  const handleVendorSplitFieldChange = (
    index: number,
    splitId: number,
    field: "vendorId" | "vendorName" | "quantity",
    value: string | number
  ) => {
    setVendorSplits((prev) => {
      const existing = prev[index] || [];
      const updated = existing.map((split) =>
        split.id === splitId ? { ...split, [field]: value } : split
      );
      return { ...prev, [index]: updated };
    });
  };

  const calculateTotals = () => {
    let totalAmount = 0;

    items.forEach((item) => {
      const quantity = item.quantity || 0;
      const price = item.price || 0;
      const taxRate = item.tax / 100 || 0;

      const itemTotal = quantity * price;
      const itemGST = itemTotal * taxRate;

      totalAmount += itemTotal;
    });

    return {
      totalAmount: totalAmount.toFixed(2),
      overallTotal: totalAmount.toFixed(2),
    };
  };

  const { totalAmount, overallTotal } = calculateTotals();
  const canEdit =
    permittedActions.includes("DRAFT") || permittedActions.includes("CREATED");

  if (isFetching) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <div className="w-full p-4">
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}>
            <CircularProgress />
          </Box>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="w-full p-4">
        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: "600",
                color: "#1E1E1E",
                textAlign: "left",
                fontSize: "1.5rem",
                mb: 0,
              }}>
              Edit Transfer Order
            </Typography>

            {params.orderId && (
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => setHistoryDialogOpen(true)}
                  sx={{ textTransform: "none", fontWeight: 500, borderRadius: "8px" }}>
                  History
                </Button>
                <Button
                  variant="contained"
                  startIcon={
                    isDownloading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={handleDownloadPdf}
                  disabled={isDownloading}
                  sx={{
                    backgroundColor: "#1366D9",
                    color: "white",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      backgroundColor: "#0f5cc0",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                    },
                    "&:disabled": {
                      backgroundColor: "#B0C4DE",
                    },
                  }}>
                  {isDownloading ? "Downloading..." : "Download PDF"}
                </Button>
              </Box>
            )}
          </Box>

          {params.orderId && (
            <TransferOrderHistory
              transferOrderId={Number(params.orderId)}
              variant="dialog"
              open={historyDialogOpen}
              onClose={() => setHistoryDialogOpen(false)}
            />
          )}

          <div className="flex items-center bg-gray-100 px-4 py-2 rounded-md space-x-2">
            <Link
              to="/home"
              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
              Dashboard
            </Link>
            <span className="text-sm text-gray-400">/</span>
            <Link
              to={`/transfer-order${statusFilter ? `?status=${statusFilter}` : ''}`}
              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
              Orders
            </Link>
            <span className="text-sm text-gray-400">/</span>
            <span className="text-sm font-medium text-gray-700">
              Edit Order
            </span>
          </div>
        </Box>

        {!canEdit && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This order cannot be edited in its current status.
          </Alert>
        )}

        <Paper
          sx={{
            p: 4,
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            backgroundColor: "white",
            maxWidth: "1600px",
            margin: "0 auto",
          }}>
          {/* Form Section */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              {/* Source Location */}
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Source Location
                  </label>
                  <Autocomplete
                    fullWidth
                    disabled={!canEdit}
                    options={warehouses}
                    getOptionLabel={(option) => option.name}
                    value={sourceWarehouse}
                    onChange={(_, newValue) => {
                      setSourceWarehouse(newValue);
                      setDestinationWarehouse(null);
                      // Reset destination warehouses when source changes
                      setDestinationWarehouses([]);
                      setDestinationPageNumber(1);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          style: { fontSize: "0.875rem" },
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: handleScroll,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Deliver To
                  </label>
                  <Autocomplete
                    fullWidth
                    disabled={!sourceWarehouse || !canEdit}
                    options={destinationWarehouses.filter(
                      (wareHouse) => wareHouse.id !== sourceWarehouse?.id
                    )}
                    getOptionLabel={(option) => option.name}
                    value={destinationWarehouse}
                    onChange={(_, newValue) => {
                      setDestinationWarehouse(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          style: { fontSize: "0.875rem" },
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: handleDestinationScroll,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Source TO
                  </label>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={transferOrderDetails?.parentId ?? "N/A"}
                    InputProps={{
                      style: { fontSize: "0.875rem" },
                      readOnly: true,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Created By
                  </label>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={transferOrderDetails?.createdBy ?? "N/A"}
                    InputProps={{
                      style: { fontSize: "0.875rem" },
                      readOnly: true,
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Items Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle1"
              sx={{
                textAlign: "left",
                fontWeight: "600",
                color: "#1E1E1E",
                mb: 2,
                fontSize: "1.125rem",
              }}>
              Items
            </Typography>

            <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: 80 }}>
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: 200 }}>
                      Product Description
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: 150 }}>
                      Reference Code
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: 220 }}>
                      Quantity
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: 150 }}>
                      Unit Of Measure
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: 150 }}>
                      Unit Price
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: 100 }}>
                      Tax
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: 150 }}>
                      Total
                    </TableCell>
                    {/* Widened from 120 to fit the "Select Vendor" button next to the delete icon */}
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", minWidth: 220 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <React.Fragment key={index}>
                      <TableRow>
                        <TableCell sx={{ fontSize: "0.875rem" }}>
                          {index + 1}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ position: "relative" }}>
                            <AppAutoCompleteWithSearchAPI
                              onChange={handlePartSelect(index)}
                              initialPageNumber={0}
                              disabled={
                                !sourceWarehouse ||
                                !destinationWarehouse ||
                                !canEdit
                              }
                              value={
                                item.id && item.id > 0
                                  ? {
                                      value: item.id.toString(),
                                      label: item.description,
                                      data: item,
                                    }
                                  : null
                              }
                              getData={function (data: {
                                currentPageNumber: number;
                                currentSearchQueryTerm: string;
                              }): Promise<{
                                options: OptionType<TransferOrderPartDetails>[];
                                hasMore: boolean;
                              }> {
                                return fetchPaginatedPartsBySearchTerm(
                                  data.currentPageNumber,
                                  data.currentSearchQueryTerm
                                );
                              }}
                            />
                            {transitWarnings[index] && (
                              <Box sx={{ mt: 1 }}>
                                <Chip
                                  icon={<InfoIcon />}
                                  label={`${transitWarnings[index].quantity} units in transit`}
                                  color="warning"
                                  size="small"
                                  sx={{ fontSize: "0.75rem" }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    color: "warning.main",
                                    mt: 0.5,
                                    fontSize: "0.7rem",
                                  }}>
                                  {transitWarnings[index].message}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={item.referenceCode}
                            variant="standard"
                            disabled
                            InputProps={{
                              style: { fontSize: "0.875rem" },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <TextField
                              value={item.quantity || ""}
                              onChange={(e) => {
                                const newItems = [...items];
                                const inputValue = e.target.value;

                                if (inputValue === "0") {
                                  return;
                                }

                                newItems[index].quantity = +inputValue;
                                setItems(newItems);
                              }}
                              onBlur={() => {
                                const newItems = [...items];
                                const quantity = newItems[index].quantity;
                                if (isNaN(quantity) || quantity < 1) {
                                  newItems[index].quantity = 1;
                                }
                                setItems(newItems);
                              }}
                              variant="standard"
                              disabled={!item.description || !canEdit}
                              InputProps={{
                                style: { fontSize: "0.875rem" },
                              }}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={!item.description || !item.quantity || !canEdit}
                              onClick={() => handleToggleSplitVendor(index)}
                              sx={{
                                width: "120px",
                                flex: "none",
                                textTransform: "none",
                                borderRadius: "20px",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                borderColor: splitActiveRows[index] ? "#1366D9" : "#D0D5DD",
                                color: splitActiveRows[index] ? "#1366D9" : "#344054",
                                backgroundColor: splitActiveRows[index] ? "#EEF4FE" : "transparent",
                                whiteSpace: "nowrap",
                                "&:hover": {
                                  borderColor: "#1366D9",
                                  backgroundColor: splitActiveRows[index] ? "#EEF4FE" : "transparent",
                                },
                              }}>
                              {splitActiveRows[index] ? "Selecting Vendor" : "Select Vendor"}
                            </Button>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={"Units"}
                            variant="standard"
                            disabled
                            InputProps={{
                              style: { fontSize: "0.875rem" },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={item.price || ""}
                            variant="standard"
                            disabled
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  ₹
                                </InputAdornment>
                              ),
                              style: { fontSize: "0.875rem" },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={item.tax || ""}
                            variant="standard"
                            disabled
                            InputProps={{
                              style: { fontSize: "0.875rem" },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={(
                              item.quantity *
                              item.price *
                              (1 + item.tax / 100)
                            ).toFixed(2)}
                            variant="standard"
                            disabled
                            InputProps={{
                              style: { fontSize: "0.875rem" },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <IconButton
                              onClick={() => handleDeleteItem(index)}
                              sx={{ color: "#FF0000" }}
                              size="small"
                              disabled={!canEdit}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {splitActiveRows[index] && (
                        <TableRow>
                          <TableCell
                            colSpan={10}
                            sx={{ backgroundColor: "#FFFFFF", padding: 1, border: "none" }}>
                            {/* When the order isn't editable, show saved vendors but block interaction */}
                            <Box
                              sx={
                                !canEdit
                                  ? { pointerEvents: "none", opacity: 0.7 }
                                  : undefined
                              }>
                              <VendorAllocation
                                index={index}
                                productId={item.id}
                                quantity={item.quantity}
                                price={item.price}
                                tax={item.tax}
                                vendorSplits={vendorSplits[index] || []}
                                vendorAllocationOptions={getVendorOptionsForRow(index, item.id)}
                                isLoadingVendorOptions={isLoadingVendorOptions}
                                onAddVendorSplitRow={handleAddVendorSplitRow}
                                onRemoveVendorSplitRow={handleRemoveVendorSplitRow}
                                onVendorSplitFieldChange={handleVendorSplitFieldChange}
                                onCancel={handleToggleSplitVendor}
                              />
                            </Box>
                            {vendorSplitErrors[index] && (
                              <Box sx={{ pb: 1, mt: 0.5 }}>
                                {Object.entries(vendorSplitErrors[index].rows).map(([splitId, message]) => {
                                  const rowNumber =
                                    (vendorSplits[index] || []).findIndex((s) => String(s.id) === splitId) + 1;
                                  return (
                                    <Typography
                                      key={splitId}
                                      variant="caption"
                                      sx={{ display: "block", color: "error.main", fontSize: "0.9rem", fontWeight: 'bold' }}>
                                      Vendor {rowNumber}: {message}
                                    </Typography>
                                  );
                                })}
                                {vendorSplitErrors[index].section && (
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block", color: "error.main", fontSize: "0.9rem", fontWeight: 'bold' }}>
                                    {vendorSplitErrors[index].section}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              <IconButton
                onClick={handleAddItem}
                disabled={!sourceWarehouse || !destinationWarehouse || !canEdit}
                sx={{
                  color:
                    !sourceWarehouse || !destinationWarehouse || !canEdit
                      ? "#ccc"
                      : "#1976D2",
                  mr: 1,
                }}
                size="small">
                <AddIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="body1"
                sx={{
                  cursor:
                    !sourceWarehouse || !destinationWarehouse || !canEdit
                      ? "not-allowed"
                      : "pointer",
                  color:
                    !sourceWarehouse || !destinationWarehouse || !canEdit
                      ? "#ccc"
                      : "#1976D2",
                  fontSize: "0.875rem",
                }}
                onClick={
                  !sourceWarehouse || !destinationWarehouse || !canEdit
                    ? undefined
                    : handleAddItem
                }>
                Add Item
              </Typography>
              {(!sourceWarehouse || !destinationWarehouse) && (
                <Typography
                  variant="caption"
                  sx={{
                    ml: 2,
                    color: "text.secondary",
                    fontSize: "0.75rem",
                  }}>
                  (Select source and destination warehouses first)
                </Typography>
              )}
              {!canEdit && sourceWarehouse && destinationWarehouse && (
                <Typography
                  variant="caption"
                  sx={{
                    ml: 2,
                    color: "text.secondary",
                    fontSize: "0.75rem",
                  }}>
                  (This order cannot be edited)
                </Typography>
              )}
            </Box>
          </Box>

          {/* Totals and Actions Section */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ mb: 2, textAlign: "right" }}>
              <Typography
                variant="body1"
                sx={{ color: "#646464", fontSize: "0.875rem" }}>
                Amount - ₹ {totalAmount}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#1E1E1E",
                  fontWeight: "600",
                  fontSize: "0.875rem",
                }}>
                Total - ₹ {overallTotal}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="contained"
                color="error"
                onClick={() => navigate(`/transfer-order${statusFilter ? `?status=${statusFilter}` : ''}`)}
                sx={{
                  padding: "8px 20px",
                  borderRadius: "32px",
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  backgroundColor: "#F94932",
                }}>
                Cancel
              </Button>
              {canEdit && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleUpdateTransferOrder("DRAFT")}
                    sx={{
                      padding: "8px 20px",
                      borderRadius: "32px",
                      backgroundColor: "#1366D9",
                      textTransform: "none",
                      fontWeight: "500",
                      fontSize: "0.875rem",
                      "&:hover": { backgroundColor: "#1366D9" },
                    }}>
                    Save as Draft
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleUpdateTransferOrder("CREATED")}
                    sx={{
                      padding: "8px 20px",
                      borderRadius: "32px",
                      backgroundColor: "#1E1E1E",
                      textTransform: "none",
                      fontWeight: "500",
                      fontSize: "0.875rem",
                      "&:hover": { backgroundColor: "#000000" },
                    }}>
                    Update
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={2000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Paper>
      </div>
    </div>
  );
};

export default DraftTransferOrder;
